import React from "react";
import { Link } from "react-router-dom";
import { FileText, Scale, Info } from "lucide-react";

export default function Terms() {
  return (
    <div>
      <section className="wv-hero-grain bg-gradient-to-b from-[#F8F9FA] to-white py-14 md:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10">
          <p className="text-xs font-bold uppercase tracking-widest text-[color:var(--wv-green-dark)]">Juridisch</p>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold text-[color:var(--wv-obsidian)] mt-2" data-testid="terms-title">Algemene voorwaarden</h1>
          <p className="mt-3 text-slate-600 max-w-2xl">Deze voorwaarden zijn van toepassing op het gebruik van het platform Woon Verder, de bemiddelingsdiensten en aanvullende services.</p>
          <p className="mt-2 text-xs text-slate-500 font-mono-num">Laatst bijgewerkt: {new Date().toLocaleDateString("nl-NL", { year: "numeric", month: "long", day: "numeric" })}</p>
        </div>
      </section>

      <article className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-10 py-12 space-y-10 text-slate-700 leading-relaxed">
        <div className="p-5 rounded-2xl bg-amber-50 border border-amber-200 flex items-start gap-3">
          <Info className="w-6 h-6 text-amber-700 flex-shrink-0 mt-1" />
          <p className="text-slate-800"><strong>Belangrijk:</strong> Deze voorwaarden gelden voor iedereen die gebruik maakt van Woon Verder (bezoekers, kopers, verkopers). Door gebruik van het platform accepteert u deze voorwaarden.</p>
        </div>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">1. Definities</h2>
          <ul className="mt-3 list-disc ml-6 space-y-1">
            <li><strong>Woon Verder</strong>: handelsnaam van BK International B.V. (KvK 92949916 · BTW NL866227118B01), gevestigd in Nederland.</li>
            <li><strong>Platform</strong>: de website en app van Woon Verder.</li>
            <li><strong>Verkoper</strong>: eigenaar van een woning die een aanvraag indient om deze via het platform aan te bieden.</li>
            <li><strong>Koper / woningzoeker</strong>: gebruiker die een zoekprofiel aanmaakt en/of interesse toont in aangeboden woningen.</li>
            <li><strong>Bemiddeling</strong>: onze werkzaamheden om verkoper en koper bij elkaar te brengen.</li>
            <li><strong>Overeenkomst</strong>: de bemiddelingsovereenkomst die verkoper en Woon Verder sluiten.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">2. Toepasselijkheid</h2>
          <p className="mt-3">Deze voorwaarden gelden voor alle diensten van Woon Verder. Afwijkingen zijn alleen geldig als deze schriftelijk zijn overeengekomen. Als een bepaling ongeldig is, blijven de overige bepalingen van kracht.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">3. Accountregistratie</h2>
          <ul className="mt-3 list-disc ml-6 space-y-1">
            <li>U moet minimaal 18 jaar zijn om een account aan te maken.</li>
            <li>U bent verantwoordelijk voor de geheimhouding van uw wachtwoord.</li>
            <li>U verstrekt correcte en actuele gegevens.</li>
            <li>Wij behouden ons het recht voor accounts te weigeren, op te schorten of te verwijderen bij misbruik of onjuiste informatie.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">4. Voor verkopers — bemiddeling</h2>
          <div className="mt-3 space-y-3">
            <p><strong>4.1 Aanmelding.</strong> Verkoper vult woninggegevens in en levert foto's aan of vraagt Woon Verder-fotografie aan.</p>
            <p><strong>4.2 Controle vóór publicatie.</strong> De woning wordt <strong>nooit automatisch gepubliceerd</strong>. Woon Verder controleert eerst de aanvraag, foto's en gegevens. Publicatie vindt pas plaats na goedkeuring.</p>
            <p><strong>4.3 Bemiddelingsovereenkomst.</strong> Voor de daadwerkelijke bemiddeling wordt een overeenkomst gesloten met o.a. de looptijd, exclusiviteit, beschermingsperiode en de vergoeding bij verkoop.</p>
            <p><strong>4.4 Vergoeding.</strong> Bij een succesvolle verkoop volgens de overeenkomst is verkoper aan Woon Verder een vergoeding verschuldigd van <strong>2,5% (excl. btw)</strong> van de gerealiseerde verkoopprijs, tenzij anders overeengekomen.</p>
            <p><strong>4.5 Introduced buyer / beschermingsperiode.</strong> Wanneer een koper via Woon Verder is geïntroduceerd, geldt de vergoeding ook indien de transactie binnen de beschermingsperiode plaatsvindt na beëindiging van de overeenkomst.</p>
            <p><strong>4.6 Aanpassing van tarief.</strong> Wijzigingen in het standaardtarief gelden alleen voor nieuwe overeenkomsten; historische transacties blijven onveranderd.</p>
          </div>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">5. Voor kopers — zoekprofiel</h2>
          <ul className="mt-3 list-disc ml-6 space-y-1">
            <li>Registratie en het aanmaken van een zoekprofiel is gratis.</li>
            <li>De match-score is indicatief en geen garantie op geschiktheid, beschikbaarheid of aankoop.</li>
            <li>Contact met verkopers verloopt via Woon Verder tenzij verkoper anders aangeeft.</li>
            <li>Optionele premium diensten (Zoekservice pakketten) hebben eigen voorwaarden en tarieven.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">6. Fotografie service</h2>
          <ul className="mt-3 list-disc ml-6 space-y-1">
            <li>Fotografie is een <strong>aparte betaalde service</strong>. Prijzen staan op <Link to="/fotografie" className="text-[color:var(--wv-navy)] font-semibold hover:underline">/fotografie</Link>.</li>
            <li>Genoemde bedragen zijn exclusief 21% btw tenzij anders vermeld.</li>
            <li>Annuleren minimaal 48 uur voor de afspraak is kosteloos. Bij annulering binnen 48 uur brengen wij 50% van de pakketprijs in rekening.</li>
            <li>Foto's blijven eigendom van Woon Verder tot volledige betaling; verkoper krijgt vervolgens een gebruikslicentie voor de woningverkoop.</li>
            <li>Levertijd zoals aangegeven in het gekozen pakket (24u of 48u na de opname).</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">7. Content en foto's</h2>
          <ul className="mt-3 list-disc ml-6 space-y-1">
            <li>Verkoper garandeert dat aangeleverde foto's en informatie juist zijn en dat verkoper alle benodigde rechten heeft.</li>
            <li>Verkoper verleent Woon Verder een niet-exclusieve licentie om deze content te gebruiken voor het aanbieden van de woning op het platform.</li>
            <li>Woon Verder mag content weigeren, aanpassen of verwijderen bij twijfel over rechtmatigheid, kwaliteit of juistheid.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">8. Verboden gebruik</h2>
          <p className="mt-3">Het is niet toegestaan om:</p>
          <ul className="mt-2 list-disc ml-6 space-y-1">
            <li>Onjuiste of misleidende woninginformatie te plaatsen.</li>
            <li>Content te plaatsen die inbreuk maakt op rechten van derden.</li>
            <li>Automatisch (scraping / bots) gegevens van het platform te verzamelen zonder toestemming.</li>
            <li>Andere gebruikers te intimideren, spammen of oplichten.</li>
            <li>Betaal- of transactieprocessen buiten Woon Verder te omzeilen om de bemiddelingsvergoeding te ontwijken.</li>
          </ul>
          <p className="mt-3">Bij overtreding kunnen wij accounts direct opschorten en juridische stappen ondernemen.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">9. Aansprakelijkheid</h2>
          <ul className="mt-3 list-disc ml-6 space-y-1">
            <li>Woon Verder is niet aansprakelijk voor onjuiste informatie die door verkopers is aangeleverd.</li>
            <li>Wij geven geen garanties over verkoopresultaat, verkooptermijn of specifieke koopprijs.</li>
            <li>Onze aansprakelijkheid is beperkt tot het bedrag van de door u aan Woon Verder betaalde vergoeding voor de dienst waaruit de schade voortvloeit, met een maximum van het uitkeringsbedrag van onze aansprakelijkheidsverzekering.</li>
            <li>Wij zijn niet aansprakelijk voor indirecte schade, gevolgschade, gemiste besparingen of gederfde winst.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">10. Geen advies</h2>
          <div className="mt-3 flex items-start gap-3 p-4 rounded-xl bg-amber-50 border border-amber-200">
            <Scale className="w-6 h-6 text-amber-700 flex-shrink-0 mt-1" />
            <p>Woon Verder verleent bemiddelingsdiensten, geen notariële, juridische, fiscale of hypotheekadviezen. Wij verwijzen u door naar bevoegde professionals waar nodig.</p>
          </div>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">11. Betaling en facturatie</h2>
          <ul className="mt-3 list-disc ml-6 space-y-1">
            <li>Betaaltermijn voor facturen is 14 dagen na factuurdatum, tenzij anders overeengekomen.</li>
            <li>Bij te late betaling zijn wettelijke rente en incassokosten verschuldigd.</li>
            <li>Alle bedragen zijn in EUR, exclusief 21% btw tenzij expliciet anders vermeld.</li>
          </ul>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">12. Beëindiging</h2>
          <p className="mt-3">U kunt uw account op ieder moment beëindigen. Voor lopende bemiddelingsovereenkomsten gelden de opzegvoorwaarden zoals vastgelegd in die overeenkomst (o.a. beschermingsperiode).</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">13. Wijzigingen</h2>
          <p className="mt-3">Wij mogen deze voorwaarden aanpassen. Materiële wijzigingen kondigen wij minimaal 30 dagen vooraf aan. Op lopende overeenkomsten blijven de op dat moment geldende voorwaarden van toepassing tenzij anders overeengekomen.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">14. Klachten</h2>
          <p className="mt-3">Wij horen graag als u ergens niet tevreden over bent. Mail uw klacht naar <a href="mailto:info@woonverder.eu" className="text-[color:var(--wv-navy)] font-semibold hover:underline">info@woonverder.eu</a>. Wij reageren binnen 2 werkdagen en streven naar een oplossing binnen 14 dagen.</p>
        </section>

        <section>
          <h2 className="font-display text-2xl font-bold text-[color:var(--wv-obsidian)]">15. Toepasselijk recht en bevoegde rechter</h2>
          <p className="mt-3">Op deze voorwaarden en op overeenkomsten met Woon Verder is Nederlands recht van toepassing. Geschillen worden voorgelegd aan de bevoegde rechter in Nederland, tenzij dwingend recht anders bepaalt.</p>
        </section>

        <div className="p-6 rounded-2xl bg-[color:var(--wv-obsidian)] text-white flex items-start gap-4">
          <FileText className="w-6 h-6 text-[color:var(--wv-green)] flex-shrink-0 mt-1" />
          <div>
            <p className="font-display font-bold text-lg">Vragen over deze voorwaarden?</p>
            <p className="text-white/80 mt-1">Mail ons via <a href="mailto:info@woonverder.eu" className="text-[color:var(--wv-green)] font-semibold underline">info@woonverder.eu</a>.</p>
          </div>
        </div>

        <p className="text-xs text-slate-500 pt-6 border-t border-slate-200">
          Zie ook onze <Link to="/privacy" className="text-[color:var(--wv-navy)] font-semibold hover:underline">privacyverklaring</Link> en <Link to="/hoe-het-werkt" className="text-[color:var(--wv-navy)] font-semibold hover:underline">hoe het werkt</Link>.
        </p>
      </article>
    </div>
  );
}
